import {
    type AuditFilterChange,
    type AuditLogEntry,
    type AuditTargetDescription,
    type AuditText,
    addAuditFilters,
    auditMessage,
    describeAuditTarget,
    scopePathTitle,
    useAuditText,
} from '@/domain/auditLog';

import CellFilterButton from './CellFilterButton';
import CellLines from './CellLines';
import ScopePath from './ScopePath';

import styles from './TargetCell.module.scss';

interface TargetCellProps {
    entry: AuditLogEntry;
    onFilter: (change: AuditFilterChange) => void;
}

// Worded like the chips the clicks add, so the hint and the result read the same.
function targetText({ type, name, stateLabel }: AuditTargetDescription): AuditText {
    const typed =
        name === null ? type : auditMessage('auditLog.chips.value.typedName', { type, name });
    return stateLabel === null
        ? typed
        : auditMessage('auditLog.chips.value.withState', { name: typed, state: stateLabel });
}

function lines(...texts: (string | undefined)[]): string | undefined {
    const shown = texts.filter((text): text is string => text !== undefined);
    return shown.length > 0 ? shown.join('\n') : undefined;
}

export default function TargetCell({ entry, onFilter }: TargetCellProps) {
    const render = useAuditText();
    const target = describeAuditTarget(entry);
    const { name, stateLabel, renamed, path, recordedLabel } = target;
    const text = render(targetText(target));
    const notFound = target.title === null ? undefined : render(target.title);
    // The line shows the name without its scope; the title keeps the label as written.
    const recorded =
        recordedLabel === null
            ? undefined
            : render(auditMessage('auditLog.target.recordedAs', { label: recordedLabel }));
    const detail = lines(notFound, recorded);

    return (
        <CellLines
            first={
                <>
                    <CellFilterButton
                        value={text}
                        detail={detail}
                        onFilter={() => onFilter(addAuditFilters(target.filter))}>
                        <span className={styles.type}>{render(target.type)}</span>
                        {name === null ? null : <span className={styles.name}>{render(name)}</span>}
                        {stateLabel === null ? null : (
                            <span className={styles.state}>{render(stateLabel)}</span>
                        )}
                    </CellFilterButton>
                    {renamed === null ? null : (
                        <span className={styles.renamed} title={render(renamed.title)}>
                            {render(renamed.marker)}
                        </span>
                    )}
                </>
            }
            firstTitle={lines(text, detail)}
            second={path.length === 0 ? null : <ScopePath segments={path} onFilter={onFilter} />}
            secondTitle={render(scopePathTitle(path))}
        />
    );
}
